import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// IST helpers
const IST_OFFSET_MIN = 330;
const istNow = () => {
  const now = new Date();
  return new Date(now.getTime() + (now.getTimezoneOffset() + IST_OFFSET_MIN) * 60000);
};
const formatISTDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);
const formatCompact = (v) => {
  if (!v) return "0";
  if (v >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return Math.round(v).toString();
};

// Color ramp by relative magnitude
const barColor = (v, max) => {
  if (!max || max <= 0) return "#3B82F6";
  const r = v / max;
  if (r > 0.8) return "#00E676";
  if (r > 0.6) return "#FFEB00";
  if (r > 0.4) return "#FF9100";
  if (r > 0.2) return "#FF6D00";
  return "#FF3D00";
};

const TooltipBox = ({ active, payload, dateLabel }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const formatNative = (v, cur) => new Intl.NumberFormat("en-IN", { style: "currency", currency: cur || "INR", maximumFractionDigits: 0 }).format(v || 0);
  // When FX is missing for foreign currency, the chart bar value is the native amount
  // (not converted INR). Display it with the native currency symbol instead of ₹.
  const showNative = d.fx_missing && d.is_foreign && d.currency && d.currency !== "INR";
  const revenueLabel = showNative ? `Revenue (Net, ${d.currency})` : "Revenue (Net, INR)";
  const revenueValue = showNative ? formatNative(d.net_sales, d.currency) : formatINR(d.net_sales);
  const grossValue = showNative ? formatNative(d.gross_sales, d.currency) : formatINR(d.gross_sales);
  return (
    <div className="bg-[#0B0B0B] border border-[#2A2A2A] rounded p-3 text-sm shadow-xl min-w-[240px]">
      <div className="font-semibold text-[#E4E4E7] mb-2 border-b border-[#2A2A2A] pb-1.5">{d.client_name}</div>
      <div className="flex justify-between gap-6 py-0.5">
        <span className="text-[#71717A]">Date</span>
        <span className="font-mono text-white">{dateLabel}</span>
      </div>
      <div className="flex justify-between gap-6 py-0.5">
        <span className="text-[#FFEB00]">{revenueLabel}</span>
        <span className="font-mono text-white">{revenueValue}</span>
      </div>
      {d.is_foreign && d.net_sales_native > 0 && !d.fx_missing && (
        <div className="flex justify-between gap-6 py-0.5">
          <span className="text-[#A1A1AA]">Native ({d.currency})</span>
          <span className="font-mono text-[#A1A1AA]">{formatNative(d.net_sales_native, d.currency)} @ {d.inr_conversion_rate}</span>
        </div>
      )}
      {d.fx_missing && (
        <div className="mt-1 px-1.5 py-1 bg-[#3A1F00] border border-[#F59E0B]/40 rounded text-[11px] text-[#F59E0B] leading-tight">
          FX rate not set — showing native {d.currency}. Set <span className="font-medium">INR Conversion Rate</span> on the client to convert to ₹.
        </div>
      )}
      {d.gross_sales > 0 && d.gross_sales !== d.net_sales && (
        <div className="flex justify-between gap-6 py-0.5">
          <span className="text-[#A1A1AA]">Gross</span>
          <span className="font-mono text-[#A1A1AA]">{grossValue}</span>
        </div>
      )}
      <div className="flex justify-between gap-6 py-0.5">
        <span className="text-[#A1A1AA]">Orders</span>
        <span className="font-mono text-white">{d.order_count || 0}</span>
      </div>
    </div>
  );
};

const DailyRevenueByClient = () => {
  const navigate = useNavigate();
  const today = useMemo(() => formatISTDate(istNow()), []);
  const minDate = useMemo(() => {
    const d = istNow();
    d.setDate(d.getDate() - 30);
    return formatISTDate(d);
  }, []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showZero, setShowZero] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/dashboard/daily-revenue-by-client`, { params: { date: selectedDate } });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Failed to load daily revenue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedDate]);

  const rows = data?.rows || [];
  const filteredRows = showZero ? rows : rows.filter(r => r.net_sales > 0);
  const maxRev = filteredRows.reduce((m, r) => Math.max(m, r.net_sales), 0);

  const dateLabel = useMemo(() => {
    try {
      const [y, m, d] = selectedDate.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric"
      });
    } catch { return selectedDate; }
  }, [selectedDate]);

  return (
    <div className="card-flat p-6 mb-6" data-testid="daily-revenue-by-client-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold">Daily Revenue by Client</h2>
          <p className="text-xs text-[#71717A] mt-1">
            Shopify revenue for each client on {dateLabel}
            {data?.is_today && <span className="text-[#00E676] ml-2">· LIVE (12 AM IST → now)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#71717A] cursor-pointer">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 accent-[#FFEB00]"
              checked={showZero}
              onChange={e => setShowZero(e.target.checked)}
              data-testid="show-zero-clients-toggle"
            />
            Show all clients (incl. zero-revenue)
          </label>
          <input
            type="date"
            className="input-default text-sm py-1.5 px-3"
            value={selectedDate}
            min={minDate}
            max={today}
            onChange={e => setSelectedDate(e.target.value)}
            data-testid="daily-revenue-date-picker"
          />
        </div>
      </div>

      {/* Summary line */}
      {!loading && !error && data && (
        <div className="flex items-center gap-6 text-xs text-[#71717A] mb-4">
          <span>Clients with revenue: <span className="text-[#E4E4E7] data-number">{data.clients_with_revenue}</span> / {data.total_clients}</span>
          <span>Total: <span className="text-[#FFEB00] data-number">{formatINR(data.total_net_sales)}</span></span>
          <span>Orders: <span className="text-[#E4E4E7] data-number">{data.total_orders}</span></span>
        </div>
      )}

      {loading && <div className="h-72 flex items-center justify-center text-[#71717A] text-sm">Loading…</div>}
      {error && <div className="h-72 flex items-center justify-center text-[#FF6666] text-sm">{error}</div>}
      {!loading && !error && filteredRows.length === 0 && (
        <div className="h-72 flex items-center justify-center text-[#71717A] text-sm">
          No Shopify revenue recorded on {dateLabel}.
        </div>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <div className="w-full" style={{ height: Math.max(360, filteredRows.length * 38 + 80) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredRows}
              layout="vertical"
              margin={{ top: 8, right: 32, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={formatCompact}
                stroke="#52525B"
                tick={{ fill: "#71717A", fontSize: 11 }}
              />
              <YAxis
                type="category"
                dataKey="client_name"
                width={160}
                stroke="#52525B"
                tick={(props) => {
                  const { x, y, payload } = props;
                  const row = filteredRows.find((r) => r.client_name === payload.value);
                  const clientId = row?.client_id;
                  const display = payload.value && payload.value.length > 15
                    ? payload.value.slice(0, 15) + "…"
                    : payload.value;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={-5}
                        y={0}
                        dy={4}
                        textAnchor="end"
                        fill="#E4E4E7"
                        fontSize={12}
                        style={{ cursor: clientId ? "pointer" : "default" }}
                        onClick={() => clientId && navigate(`/clients/${clientId}`)}
                        onMouseEnter={(e) => { if (clientId) e.target.style.fill = "#FFEB00"; }}
                        onMouseLeave={(e) => { e.target.style.fill = "#E4E4E7"; }}
                      >
                        {display}
                      </text>
                    </g>
                  );
                }}
                interval={0}
              />
              <Tooltip
                cursor={{ fill: "rgba(255,235,0,0.05)" }}
                content={<TooltipBox dateLabel={dateLabel} />}
              />
              <Bar dataKey="net_sales" radius={[0, 4, 4, 0]} barSize={22}>
                {filteredRows.map((row, idx) => (
                  <Cell key={row.client_id || idx} fill={barColor(row.net_sales, maxRev)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default DailyRevenueByClient;
